'use client';

import Script from 'next/script';
import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, Suspense, memo } from 'react';

// GTM Configuration
const GTM_ID = process.env.NEXT_PUBLIC_GTM_ID || 'GTM-P77M7W63'; // Your GTM container ID
const GTM_AUTH = process.env.NEXT_PUBLIC_GTM_AUTH || ''; // Optional: for GTM environments
const GTM_PREVIEW = process.env.NEXT_PUBLIC_GTM_PREVIEW || ''; // Optional: for GTM environments

declare global {
  interface Window {
    dataLayer: any[];
    __gtmDebug?: any[];
  }
}

/**
 * Push events to dataLayer for GTM
 */
export function pushToDataLayer(data: Record<string, any>) {
  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(data);
    // Always log for debugging (remove this after confirming events work)
    console.log('[GTM] Pushed to dataLayer:', data);

    // Also add to window for debugging
    if (!window.__gtmDebug) {
      window.__gtmDebug = [];
    }
    window.__gtmDebug.push({
      timestamp: new Date().toISOString(),
      data: data
    });
  }
}

/**
 * Track custom events
 */
export function trackGTMEvent(
  eventName: string,
  parameters?: Record<string, any>
) {
  pushToDataLayer({
    event: eventName,
    ...parameters,
    // Add timestamp for debugging
    event_timestamp: new Date().toISOString()
  });
}


/**
 * Track user-agnostic session properties (no PII).
 */
export function setUserProperties(userType?: string) {
  pushToDataLayer({
    event: 'user_data',
    user_type: userType, // 'client' or 'therapist'
  });
}

/**
 * Component to track page views
 */
function GTMPageTracker() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    // Track page view when route changes
    const url = pathname + (searchParams.toString() ? `?${searchParams.toString()}` : '');

    pushToDataLayer({
      event: 'page_view',
      page_path: pathname,
      page_location: url,
      page_title: document.title
    });
  }, [pathname, searchParams]);

  return null;
}

/** Paths where third-party analytics MUST NOT load because the URL
 *  fragment carries a live security credential. GTM's bundled GA4
 *  tag calls document.location.href on init — that fires before
 *  client-side JS can strip the fragment, and Google receives the
 *  live token. Blocking the whole GTM injection here is simpler and
 *  more robust than trying to race the fragment strip.
 *
 *  Members:
 *    /mfa/recover        — MFA recovery token (single-use, 60m TTL)
 *    /auth/magic-callback — Supabase access + refresh tokens (much
 *                           higher value than MFA recovery — full
 *                           platform session credentials) */
const SKIP_TRACKING_PREFIXES = ['/mfa/recover', '/auth/magic-callback'];

function GoogleTagManager() {
  const pathname = usePathname();
  const skipTracking = !!pathname && SKIP_TRACKING_PREFIXES.some((p) => pathname.startsWith(p));

  // Initialize dataLayer early
  useEffect(() => {
    if (skipTracking) return;
    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || [];
      // Log initialization only once on mount
      console.log('[GTM] Initialized with:', {
        GTM_ID,
        hasGtmId: !!GTM_ID
      });
    }
  }, [skipTracking]); // Empty deps would skip re-check on navigation; skipTracking is stable per path

  if (!GTM_ID || skipTracking) {
    return null;
  }

  // Build GTM script URL - Load directly from Google
  const gtmScriptUrl = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;

  // Build final script URL with auth parameters if provided
  let finalGtmScriptUrl = gtmScriptUrl;
  if (GTM_AUTH && GTM_PREVIEW) {
    finalGtmScriptUrl += `&gtm_auth=${GTM_AUTH}&gtm_preview=${GTM_PREVIEW}&gtm_cookies_win=x`;
  }

  return (
    <>
      {/* Google Tag Manager Script */}
      <Script
        id="gtm-script"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            '${finalGtmScriptUrl}'+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','${GTM_ID}');
          `,
        }}
      />

      {/* Google Tag Manager noscript */}
      <noscript>
        <iframe
          src={`https://www.googletagmanager.com/ns.html?id=${GTM_ID}${
            GTM_AUTH && GTM_PREVIEW
              ? `&gtm_auth=${GTM_AUTH}&gtm_preview=${GTM_PREVIEW}&gtm_cookies_win=x`
              : ''
          }`}
          height="0"
          width="0"
          style={{ display: 'none', visibility: 'hidden' }}
        />
      </noscript>

      {/* Page view tracker */}
      <Suspense fallback={null}>
        <GTMPageTracker />
      </Suspense>
    </>
  );
}

// Memoize the component to prevent unnecessary re-renders
export default memo(GoogleTagManager);

/**
 * Standard e-commerce event names for consistency
 */
// GA4 Standard Events that match our Meta tracking
export const GTMEvents = {
  // Core events matching Meta tracking
  QUIZ_START: 'begin_checkout', // Maps to Meta's InitiateCheckout
  QUIZ_COMPLETE: 'generate_lead', // Maps to Meta's Lead event
  BOOKING_COMPLETE: 'purchase', // Maps to Meta's Schedule event

  // User actions
  SIGN_UP: 'sign_up',
  LOGIN: 'login',
};

// All tracking helpers are zero-PII — no userId, email, or phone passed to GTM.

export function trackQuizStart(
  quizId: string,
  sessionType: string,
  therapistType: string,
  rate: number,
) {
  trackGTMEvent(GTMEvents.QUIZ_START, {
    rate: rate || 0,
    type: sessionType,
    tier: therapistType,
    quizId: quizId,
  });
}

export function trackQuizCompletion(
  quizId: string,
  sessionType?: string,
  therapistType?: string,
  rate?: number,
) {
  const eventData: Record<string, any> = {
    rate: rate,
    type: sessionType,
    tier: therapistType,
    quizId: quizId,
  };

  Object.keys(eventData).forEach(key => {
    if (eventData[key] === undefined) {
      delete eventData[key];
    }
  });

  trackGTMEvent(GTMEvents.QUIZ_COMPLETE, eventData);
}

export function trackBookingCompletion(
  sessionId: string,
  sessionType: string,
  rate: number,
  therapistType?: string,
  quizId?: string,
) {
  trackGTMEvent(GTMEvents.BOOKING_COMPLETE, {
    transaction_id: sessionId,
    rate: rate || 0,
    type: sessionType,
    tier: therapistType,
    sessionId: sessionId,
    quizId: quizId,
  });
}